import {
  type GridView,
  useFetchGridViews,
  useRetainedState
} from "@appretio/shared";
import { useCallback, useMemo } from "react";
import {
  type CubePage,
  type CubeSelectionState,
  pageScope
} from "../hooks/useCubeSelectionState";
import { useOpenOnce } from "../hooks/useOpenOnce";
import {
  type ActiveView,
  useGridViewWrites
} from "../savedViews/useGridViewWrites";
import {
  CONTEXT_VIEWS_GRID,
  type ContextViewPayload,
  parseContextViewPayload,
  sameContextSelection,
  serializeContextViewPayload
} from "./contextViewPayload";

/** Inputs the hook needs to drive the Context picker. */
export interface UseContextViewStateParams {
  /** The page whose selections the picker sets and remembers. */
  page: CubePage;
  /** The page's live context and store-status selections. */
  selection: CubeSelectionState;
}

/**
 * The Context layer: which context view is applied, whether the selections
 * have drifted from it, and the user's context views — one list for every
 * cube page.
 *
 * A fresh session opens each page on the default context view, if the user
 * has one; coming back to a page keeps whatever it had, view and drift
 * included. Applying a view selects and stops — Apply stays the planner's.
 */
export function useContextViewState({
  page,
  selection
}: UseContextViewStateParams) {
  const {
    selectedContextOptions,
    setSelectedContextOptions,
    selectedStoreStatuses,
    setSelectedStoreStatuses
  } = selection;
  const { data: savedViews, isLoading } = useFetchGridViews(CONTEXT_VIEWS_GRID);
  const views = useMemo(() => savedViews ?? [], [savedViews]);
  const [activeView, setActiveView] = useRetainedState<ActiveView<never>>(
    pageScope(page),
    "contextView",
    { kind: "none" }
  );

  const activeSavedView =
    activeView.kind === "saved"
      ? views.find((view) => view.view_id === activeView.viewId)
      : undefined;
  const activePayload = useMemo(
    (): ContextViewPayload | null =>
      activeSavedView
        ? parseContextViewPayload(activeSavedView.control_state)
        : null,
    [activeSavedView]
  );

  const applySavedView = useCallback(
    (view: GridView) => {
      setActiveView({ kind: "saved", viewId: view.view_id });
      const payload = parseContextViewPayload(view.control_state);
      if (!payload) return;
      setSelectedContextOptions(payload.context);
      setSelectedStoreStatuses(payload.storeStatuses);
    },
    [setActiveView, setSelectedContextOptions, setSelectedStoreStatuses]
  );

  const openOnDefault = useCallback(() => {
    const defaultView = views.find((view) => view.is_default);
    if (defaultView) applySavedView(defaultView);
  }, [views, applySavedView]);
  // Once the views have landed — not merely stopped loading, so a failed
  // fetch doesn't spend the one shot the user's default had.
  useOpenOnce(
    pageScope(page),
    "contextView",
    savedViews !== undefined,
    openOnDefault
  );

  const capture = useCallback(
    () =>
      serializeContextViewPayload({
        context: selectedContextOptions,
        storeStatuses: selectedStoreStatuses
      }),
    [selectedContextOptions, selectedStoreStatuses]
  );
  const writes = useGridViewWrites(
    CONTEXT_VIEWS_GRID,
    { forSave: capture, forRefresh: capture },
    activeSavedView,
    setActiveView
  );
  // A context view has one section, so a save takes no section toggles.
  const { saveCurrentView: saveSections } = writes;
  const saveCurrentView = useCallback(
    (name: string, isDefault: boolean) =>
      saveSections(name, undefined, isDefault),
    [saveSections]
  );

  return {
    ...writes,
    saveCurrentView,
    views,
    isLoading,
    activeView,
    activeSavedView,
    isDirty: activePayload
      ? !sameContextSelection(activePayload, {
          context: selectedContextOptions,
          storeStatuses: selectedStoreStatuses
        })
      : false,
    applySavedView
  };
}

/** The Context layer, as the page and its picker consume it. */
export type ContextViewState = ReturnType<typeof useContextViewState>;
